import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import api from "@/lib/api";
import { User, Mail, Phone, MapPin, FileText, Package, Receipt } from "lucide-react";

/**
 * Side drawer showing full customer context: contact info, shipping address,
 * lifetime stats, and the complete order history. Opened by clicking a customer
 * email in /admin/orders or the orders count in /admin/customers.
 *
 * Pass EITHER `customerId` (direct lookup via /admin/customers/{id}) OR
 * `email` (resolve by search). Set to null/empty to close.
 */
export default function CustomerDrawer({ customerId, email, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null); setError(null); setLoading(true);
    const run = async () => {
      try {
        let cid = customerId;
        if (!cid && email) {
          // Resolve by email → pick exact match (search is case-insensitive regex).
          const r = await api.get("/admin/customers", { params: { search: email } });
          const match = (r.data || []).find((c) => (c.email || "").toLowerCase() === email.toLowerCase());
          if (!match) {
            setError("Nessun cliente registrato con questa email. L'ordine potrebbe essere stato fatto come ospite.");
            setLoading(false); return;
          }
          cid = match.id;
        }
        if (!cid) { setError("ID cliente mancante"); setLoading(false); return; }
        const r = await api.get(`/admin/customers/${cid}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Errore caricamento cliente");
      } finally { setLoading(false); }
    };
    run();
  }, [open, customerId, email]);

  const customer = data?.customer;
  const orders = data?.orders || [];
  const lifetimeValue = orders.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="customer-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-admin">
            <User className="w-5 h-5" /> Scheda cliente
          </SheetTitle>
        </SheetHeader>

        {loading && <div className="py-10 text-center text-xd-muted text-sm">Caricamento…</div>}
        {error && <div className="py-10 text-center text-xd-muted text-sm" data-testid="drawer-error">{error}</div>}

        {customer && (
          <div className="mt-6 space-y-5">
            {/* Identity */}
            <div className="bg-xd-light border border-xd-border rounded-xl p-4">
              <div className="font-admin font-semibold text-base">{customer.name || customer.full_name || "—"}</div>
              <div className="mt-2 space-y-1.5 text-xs text-xd-muted">
                <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> <span className="font-mono-tech text-xd-text">{customer.email}</span></div>
                {customer.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {customer.phone}</div>}
                {(customer.address_line || customer.city) && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-3.5 h-3.5 mt-0.5" />
                    <span>{[customer.address_line, customer.civic_number].filter(Boolean).join(" ")}, {[customer.postal_code, customer.city].filter(Boolean).join(" ")} {customer.country || ""}</span>
                  </div>
                )}
                {customer.tax_code && <div className="flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> <span className="font-mono-tech">{customer.tax_code}</span></div>}
                <div className="pt-1"><span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 uppercase">{customer.role || "customer"}</span></div>
              </div>
            </div>

            {/* Lifetime stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white border border-xd-border rounded-xl p-4">
                <div className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted">Totale speso</div>
                <div className="font-display text-2xl font-semibold mt-1" data-testid="drawer-lifetime-value">€ {lifetimeValue.toFixed(2)}</div>
              </div>
              <div className="bg-white border border-xd-border rounded-xl p-4">
                <div className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted">Ordini totali</div>
                <div className="font-display text-2xl font-semibold mt-1" data-testid="drawer-order-count">{orders.length}</div>
              </div>
            </div>

            {/* Order history */}
            <div>
              <div className="flex items-center gap-2 mb-3 text-xs font-mono-tech uppercase tracking-widest text-xd-muted">
                <Package className="w-3.5 h-3.5" /> Storico ordini
              </div>
              {orders.length === 0 && <div className="text-sm text-xd-muted py-6 text-center bg-xd-light rounded-lg">Nessun ordine ancora.</div>}
              <div className="space-y-2">
                {orders.map((o) => (
                  <div key={o.id} className="bg-white border border-xd-border rounded-lg p-3 flex items-center gap-3 text-sm" data-testid={`drawer-order-${o.order_number}`}>
                    <Receipt className="w-4 h-4 text-xd-muted shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-tech text-xs text-xd-text">{o.order_number}</div>
                      <div className="text-[11px] text-xd-muted truncate">{new Date(o.created_at).toLocaleDateString("it-IT")} · {o.payment_method} · {o.status}</div>
                    </div>
                    <div className="font-semibold">€ {(o.total || 0).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
